import { isEligibleForOnsiteTherapy } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/onsiteTherapyUtils'
import { INDIVIDUAL_COACHING_TOOLKIT } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/constants'
import { WELLNESS_TOOLKIT_PROMOTION } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/types'

import { CareOptionAvailabilityInfo } from './types'
import { AuroraCareOption } from '../../careOptionsMetadata/auroraCareOptionsMetadata'

const CARE_OPTION_FLAG_MAP: { key: keyof typeof FLAG_TO_BOOLEAN_FN; careOption: AuroraCareOption }[] = [
  { key: 'showSelfSearch', careOption: AuroraCareOption.SELF_TRIAGE },
  { key: 'showChildSearch', careOption: AuroraCareOption.CHILD_TRIAGE },
  { key: 'showCoachingToolkit', careOption: AuroraCareOption.MENTAL_HEALTH_COACHING },
  { key: 'showParentingToolkit', careOption: AuroraCareOption.PARENTING_COACHING },
  { key: 'showCouplesTherapy', careOption: AuroraCareOption.COUPLES_THERAPY },
  { key: 'showFamilyTherapy', careOption: AuroraCareOption.FAMILY_THERAPY },
  { key: 'showMedsConsult', careOption: AuroraCareOption.MEDICATION_CONSULTATION },
  { key: 'showOnsiteTherapy', careOption: AuroraCareOption.ONSITE_THERAPY },
]

/**
 * Only SELF_SEARCH is teen-enabled among the care options shown in the carousel.
 * Maps from TEEN_ENABLED_PATHWAYS in shared-app-logic.
 */
const TEEN_ENABLED_CARE_OPTIONS: AuroraCareOption[] = [AuroraCareOption.SELF_TRIAGE]

const FLAG_TO_BOOLEAN_FN = {
  showSelfSearch: (info: CareOptionAvailabilityInfo) => info.selfSearchAvailable,
  showChildSearch: (info: CareOptionAvailabilityInfo) => !info.hideChildSearchCTA,
  showCoachingToolkit: (info: CareOptionAvailabilityInfo) =>
    info.availabilityByToolkit[INDIVIDUAL_COACHING_TOOLKIT]?.isToolkitAvailable ?? false,
  showParentingToolkit: (info: CareOptionAvailabilityInfo) =>
    info.availabilityByToolkit[WELLNESS_TOOLKIT_PROMOTION.PARENT]?.isToolkitAvailable ?? false,
  showMedsConsult: (info: CareOptionAvailabilityInfo) =>
    !info.isInternationalUser && info.isMedicationConsultationAvailable,
  showCouplesTherapy: (info: CareOptionAvailabilityInfo) =>
    info.isNewForkedTriageEnabled && info.isDirectAccessTherapySupported && info.selfSearchAvailable,
  showFamilyTherapy: (info: CareOptionAvailabilityInfo) =>
    info.isNewForkedTriageEnabled && info.isDirectAccessTherapySupported && info.selfSearchAvailable,
  showOnsiteTherapy: (info: CareOptionAvailabilityInfo) =>
    isEligibleForOnsiteTherapy({
      isOnsiteEligibleResponseEnabled: info.isOnsiteEligibleResponseEnabled,
      isOnsiteEligible: info.isOnsiteEligible,
      eligibleOnsiteAddressCount: info.eligibleOnsiteAddressCount,
    }) &&
    (info.onsiteProviderCount > 0 || (info.isOnsiteEntrypointsEnabled && !info.loadingOnsiteProviders)),
}

/**
 * Returns the list of AuroraCareOption values that should be shown based on
 * a defined set of visibility rules for each care option.
 */
export const getAvailableCareOptions = (info: CareOptionAvailabilityInfo): AuroraCareOption[] => {
  if (info.isDBTCustomer) {
    const dbtOptions: AuroraCareOption[] = [AuroraCareOption.DBT_INTAKE]
    if (info.isDBTTeensEnabled) {
      dbtOptions.push(AuroraCareOption.DBT_TEEN)
    }
    return dbtOptions
  }
  return CARE_OPTION_FLAG_MAP.filter(
    ({ key, careOption }) =>
      FLAG_TO_BOOLEAN_FN[key](info) && (!info.isUserTeen || TEEN_ENABLED_CARE_OPTIONS.includes(careOption)),
  ).map(({ careOption }) => careOption)
}
